import React, { useEffect, useState } from "react";
import { Modal, Button, Alert, Typography, Spin } from "antd";
import { CheckCircleOutlined, LoadingOutlined } from "@ant-design/icons";

import {
	AssignedForm, Form as FormType, FormElement,
	FieldResponse,
	getPractitionerFormToRender,
	uploadPractitionerFormResults,
	savePractitionerFormProgress,
	getPractitionerFormProgress,
} from "../../../api/formApi";
import { uploadFiles } from "../../../api/file/fileServiceApi";
import { toFileResponses } from "../../../utils/forms/formFileResponses";
import { isFormDone } from "../../../utils/forms/formStatus";
import { useNotifications } from "../../../context/NotificationContext";
import UnifiedFormRenderer, { flattenFields } from "../../../components/forms/FormRenderer";

const { Title, Paragraph, Text } = Typography;

interface PractitionerFillFormModalProps {
	isOpen: boolean;
	onClose: () => void;
	assignedFormId: number;
	patientName?: string;
}

/**
 * Practitioner form-completion modal (forms-in-tray / dashboard). Thin wrapper
 * around the shared `UnifiedFormRenderer` (compact, wizard) — owns loading,
 * progress save/restore, file upload + submission, and the completion state.
 */
const PractitionerFillFormModal: React.FC<PractitionerFillFormModalProps> = ({
	isOpen,
	onClose,
	assignedFormId,
	patientName,
}) => {
	const [formData, setFormData] = useState<FormType | null>(null);
	const [assigned, setAssigned] = useState<AssignedForm | null>(null);
	const [responses, setResponses] = useState<Record<string | number, any>>({});
	const [uploadedFiles, setUploadedFiles] = useState<Record<string | number, File[]>>({});
	const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isComplete, setIsComplete] = useState(false);
	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		if (!isOpen) return;
		setIsLoading(true);
		setIsComplete(false);
		setResponses({});
		setUploadedFiles({});
		setCurrentSectionIndex(0);

		getPractitionerFormToRender(assignedFormId)
			.then(({ form, assigned }) => {
				// A `Reviewed` assignment is already submitted — see the note in `patient/Form.tsx`.
				if (isFormDone(assigned.status)) {
					setIsComplete(true);
					return;
				}
				const parsedStructure = form.structure
					? (typeof form.structure === "string" ? JSON.parse(form.structure) : form.structure)
					: [];
				setFormData({ ...form, structure: parsedStructure });
				setAssigned(assigned);
				return getPractitionerFormProgress(assignedFormId);
			})
			.then((progressData) => {
				if (progressData?.progress) {
					const saved = typeof progressData.progress === "string"
						? JSON.parse(progressData.progress)
						: progressData.progress;
					setResponses(saved.responses || {});
					setCurrentSectionIndex(saved.currentSection || 0);
				}
			})
			.catch(() => {})
			.finally(() => setIsLoading(false));
	}, [isOpen, assignedFormId]);

	const saveProgress = async (sectionIndex: number) => {
		if (!assigned) return;
		setIsSaving(true);
		try {
			await savePractitionerFormProgress(assigned.id, JSON.stringify({
				responses,
				currentSection: sectionIndex,
				lastSaved: new Date().toISOString(),
			}));
		} catch {}
		finally { setIsSaving(false); }
	};

	const handleSectionChange = (index: number) => {
		setCurrentSectionIndex(index);
		saveProgress(index);
	};

	const handleSubmit = async () => {
		if (!formData || !assigned) return;
		setIsSubmitting(true);
		try {
			const fieldResponses: FieldResponse[] = [];
			const allFields = flattenFields(formData.structure, !!formData.use_sections);

			for (const field of allFields) {
				if (field.type === "heading" || field.type === "paragraph") continue;
				let value = responses[field.id];
				if (field.type === "file") {
					const files = uploadedFiles[field.id] || [];
					// `POST /files` answers `{ files: [...] }`, not a bare array. Casting that
					// object to FileResponse[] compiles and stores a non-array, so the read-only
					// branch falls through `Array.isArray` and every filename and id is lost.
					value = files.length > 0
						? toFileResponses(await uploadFiles(files, assigned.patient_id), assigned.patient_id)
						: [];
				}
				// `question` only — falling back to `label` persisted the palette default
				// ("Single Select") into form_responses, and from there into the PDF export
				// and the AI prompt via QuestionnaireDataSource::formatForPrompt().
				fieldResponses.push({ id: field.id, type: field.type, question: field.question || "", response: value ?? null });
			}

			await uploadPractitionerFormResults(assigned.id, JSON.stringify(fieldResponses));
			showSuccess("Form submitted successfully");
			setIsComplete(true);
		} catch {
			showError("Failed to submit form");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			open={isOpen}
			onCancel={onClose}
			width={800}
			destroyOnClose
			title={formData?.title || "Fill Form"}
			footer={isComplete ? <Button onClick={onClose}>Close</Button> : null}
		>
			{isLoading ? (
				<div style={{ textAlign: "center", padding: 48 }}>
					<Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} />} />
				</div>
			) : isComplete ? (
				<div style={{ textAlign: "center", padding: 32 }}>
					<CheckCircleOutlined style={{ fontSize: 48, color: "var(--mg-success)" }} />
					<Title level={4} style={{ marginTop: 16 }}>Form Submitted</Title>
					<Paragraph type="secondary">This form has been completed successfully.</Paragraph>
				</div>
			) : formData ? (
				<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					{patientName && (
						<Alert message={`Regarding patient: ${patientName}`} type="info" showIcon />
					)}
					{isSaving && <Text type="secondary" style={{ fontSize: 12 }}>Saving…</Text>}
					<UnifiedFormRenderer
						structure={formData.structure}
						useSections={!!formData.use_sections}
						values={responses}
						onValuesChange={setResponses}
						files={uploadedFiles}
						onFilesChange={setUploadedFiles}
						sectionMode="wizard"
						initialSection={currentSectionIndex}
						onSectionChange={handleSectionChange}
						onSubmit={handleSubmit}
						submitting={isSubmitting}
						submitLabel="Submit"
					/>
				</div>
			) : null}
		</Modal>
	);
};

export default PractitionerFillFormModal;
